import { useMemo, useState } from "react";
import { confirm } from "../../../components/ui/Dialog";
import { Button } from "../../../components/ui/Button";
import { Segmented, Switch } from "../../../components/ui/Controls";
import { Input, Textarea } from "../../../components/ui/Input";
import { Select, SettingsGroup, SettingsRow, UnsavedBar } from "../../../components/ui/Settings";
import { Avatar, CommunityIcon } from "../../../components/ui/Avatar";
import { cn } from "../../../lib/cn";
import { COMMUNITY_ACCENTS } from "../../../lib/palette";
import { IconCheck, IconCrown, IconGlobe, IconHash, IconLock, IconX } from "../../../components/icons";
import { CommunityBanner, communityAccentProps } from "../CommunityBanner";
import { useMembers } from "../hooks";
import { useDraft, usePatchCommunity, type SectionProps } from "./shared";

/* ───────────── Appearance ───────────── */

export function AppearanceSection({ structure }: SectionProps) {
  const c = structure.community;
  const patch = usePatchCommunity(c.id);
  const textChannels = structure.channels.filter((ch) => ch.type !== "voice" && ch.type !== "stage");
  // Welcome messages can only go to plain text or announcement channels; a deleted one counts as off.
  const systemChannels = structure.channels.filter((ch) => ch.type === "text" || ch.type === "announcement");
  const joinChannelId = systemChannels.some((ch) => ch.id === c.joinAnnounceChannelId) ? c.joinAnnounceChannelId! : null;
  const d = useDraft({ accentColor: c.accentColor, showBannerInSidebar: c.showBannerInSidebar, defaultChannelId: c.defaultChannelId, joinAnnounceChannelId: joinChannelId });
  const [hex, setHex] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    await patch(d.changed);
    setSaving(false);
  };
  const validHex = /^#[0-9a-fA-F]{6}$/.test(hex);

  return (
    <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_280px]">
      <div className="min-w-0">
        <SettingsGroup title="Accent colour" description="Tints buttons, highlights and mentions inside this community for everyone.">
          <div className="px-4 py-4">
            <div role="radiogroup" aria-label="Accent colour" className="flex flex-wrap items-center gap-2">
              <button
                role="radio"
                aria-checked={d.draft.accentColor === null}
                onClick={() => d.set("accentColor", null)}
                className={cn(
                  "flex h-8 items-center gap-1.5 rounded-full border border-line-strong px-3 text-sm text-fg-2",
                  d.draft.accentColor === null && "ring-2 ring-fg ring-offset-2 ring-offset-bg",
                )}
              >
                Member's own
              </button>
              {COMMUNITY_ACCENTS.map((a) => (
                <button
                  key={a}
                  role="radio"
                  aria-checked={d.draft.accentColor === a}
                  aria-label={a}
                  onClick={() => d.set("accentColor", a)}
                  className={cn("flex size-8 items-center justify-center rounded-full", d.draft.accentColor === a && "ring-2 ring-fg ring-offset-2 ring-offset-bg")}
                  style={{ background: a }}
                >
                  {d.draft.accentColor === a && <IconCheck className="icon size-4 text-white" aria-hidden />}
                </button>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-2">
              <span className="size-8 shrink-0 rounded-full border border-line-strong" style={{ background: validHex ? hex : d.draft.accentColor ?? "transparent" }} aria-hidden />
              <Input
                value={hex}
                onChange={(e) => setHex(e.target.value.trim())}
                placeholder={d.draft.accentColor ?? "#4cc38a"}
                aria-label="Custom hex colour"
                className="!h-8 max-w-36 font-mono text-sm"
                maxLength={7}
              />
              <Button size="sm" disabled={!validHex} onClick={() => (d.set("accentColor", hex.toLowerCase()), setHex(""))}>
                Use colour
              </Button>
            </div>
          </div>
        </SettingsGroup>

        <SettingsGroup title="Layout">
          <SettingsRow title="Banner above channels" description={c.bannerUrl ? "Show the banner image at the top of the channel list." : "Show the banner image at the top of the channel list. Upload one in Profile first."} htmlFor="ap-banner">
            <Switch id="ap-banner" label="Banner above channels" checked={d.draft.showBannerInSidebar} onCheckedChange={(v) => d.set("showBannerInSidebar", v)} />
          </SettingsRow>
          <SettingsRow title="Landing channel" description="Where members arrive when they open the community for the first time.">
            <Select
              aria-label="Landing channel"
              value={d.draft.defaultChannelId ?? ""}
              onChange={(v) => d.set("defaultChannelId", v || null)}
              options={[{ value: "", label: "First channel" }, ...textChannels.map((ch) => ({ value: ch.id, label: `#${ch.name}` }))]}
            />
          </SettingsRow>
        </SettingsGroup>

        <SettingsGroup title="System messages" description="Atrium posts these as the member, so everyone can say hi.">
          <SettingsRow
            title="Welcome new members"
            description={systemChannels.length ? "Post a random welcome message when someone joins." : "Create a text channel to post welcome messages in."}
            htmlFor="ap-join"
            disabled={!systemChannels.length}
          >
            <Switch
              id="ap-join"
              label="Welcome new members"
              checked={d.draft.joinAnnounceChannelId !== null}
              disabled={!systemChannels.length}
              onCheckedChange={(v) =>
                d.set("joinAnnounceChannelId", v ? (systemChannels.find((ch) => ch.id === c.defaultChannelId) ?? systemChannels.find((ch) => ch.type === "text") ?? systemChannels[0]).id : null)
              }
            />
          </SettingsRow>
          {d.draft.joinAnnounceChannelId !== null && (
            <SettingsRow title="System messages channel" description="Where welcome messages are posted.">
              <Select
                aria-label="System messages channel"
                value={d.draft.joinAnnounceChannelId}
                onChange={(v) => d.set("joinAnnounceChannelId", v || null)}
                options={systemChannels.map((ch) => ({ value: ch.id, label: `#${ch.name}` }))}
              />
            </SettingsRow>
          )}
        </SettingsGroup>
        <UnsavedBar show={d.dirty} saving={saving} onReset={d.reset} onSave={() => void save()} />
      </div>

      <aside className="lg:sticky lg:top-0 lg:self-start">
        <div className="mb-2 text-xs font-medium text-fg-3">Preview</div>
        <div className="overflow-hidden rounded-xl border border-line bg-bg-subtle" {...communityAccentProps(d.draft.accentColor)}>
          {d.draft.showBannerInSidebar && c.bannerUrl && <CommunityBanner url={c.bannerUrl} preset={c.bannerColor} className="h-16" />}
          <div className="flex h-10 items-center gap-2 border-b border-line px-3 text-sm font-semibold">
            <CommunityIcon src={c.iconUrl} name={c.name} size="xs" />
            <span className="truncate">{c.name}</span>
          </div>
          <div className="space-y-px p-2 text-sm">
            {textChannels.slice(0, 3).map((ch, i) => (
              <div key={ch.id} className={cn("flex h-8 items-center gap-1.5 rounded-md px-2", i === 0 ? "bg-accent-soft text-fg" : "text-fg-3")}>
                <IconHash className="icon size-4" aria-hidden />
                {ch.name}
              </div>
            ))}
            <div className="flex items-center gap-2 px-2 pt-2">
              <span className="rounded bg-mention px-1 text-accent">@you</span>
              <span className="h-7 rounded-md bg-accent px-3 text-xs font-medium leading-7 text-accent-fg">Join voice</span>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

/* ───────────── Access ───────────── */

export function AccessSection({ structure }: SectionProps) {
  const c = structure.community;
  const patch = usePatchCommunity(c.id);
  const d = useDraft({ visibility: c.visibility, publishToFeed: c.publishToFeed, showMembersPublicly: c.showMembersPublicly, rules: c.rules });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (d.changed.visibility === "private") {
      const ok = await confirm({
        title: "Make this community private?",
        description: "It will disappear from discovery, profiles and the feed. Followers will be removed and announcements published as the community will be hidden.",
        confirmLabel: "Make private",
        danger: true,
      });
      if (!ok) return;
    }
    setSaving(true);
    await patch(d.changed);
    setSaving(false);
  };

  return (
    <div>
      <SettingsGroup title="Who can join">
        <SettingsRow title="Visibility" description={d.draft.visibility === "public" ? "Anyone can find, preview and join." : "Hidden. People join with an invite."}>
          <Segmented
            ariaLabel="Visibility"
            size="sm"
            value={d.draft.visibility}
            onChange={(v) => d.set("visibility", v)}
            options={[
              { value: "public", label: "Public", icon: IconGlobe },
              { value: "private", label: "Private", icon: IconLock },
            ]}
          />
        </SettingsRow>
        <SettingsRow title="Public member list" description="Show who's a member on the public page." htmlFor="ac-members">
          <Switch id="ac-members" label="Public member list" checked={d.draft.showMembersPublicly} onCheckedChange={(v) => d.set("showMembersPublicly", v)} />
        </SettingsRow>
        <SettingsRow title="Publish to the feed" description="Let roles with Publish to feed post as the community." htmlFor="ac-feed" disabled={d.draft.visibility !== "public"}>
          <Switch
            id="ac-feed"
            label="Publish to the feed"
            disabled={d.draft.visibility !== "public"}
            checked={d.draft.publishToFeed && d.draft.visibility === "public"}
            onCheckedChange={(v) => d.set("publishToFeed", v)}
          />
        </SettingsRow>
      </SettingsGroup>

      <SettingsGroup title="Rules" description="Shown on the public page and invite screen. Plain text, one rule per line works well.">
        <div className="p-3">
          <Textarea
            value={d.draft.rules}
            onChange={(e) => d.set("rules", e.target.value)}
            rows={6}
            maxLength={2000}
            placeholder={"Be kind.\nKeep it on topic.\nNo spam or self-promotion."}
            aria-label="Community rules"
          />
        </div>
      </SettingsGroup>

      <UnsavedBar show={d.dirty} saving={saving} onReset={d.reset} onSave={() => void save()} />

      {c.isOwner && <TransferOwnership structure={structure} />}
    </div>
  );
}

function TransferOwnership({ structure }: { structure: SectionProps["structure"] }) {
  const c = structure.community;
  const patch = usePatchCommunity(c.id);
  const { data } = useMembers(c.id);
  const [q, setQ] = useState("");
  const members = useMemo(
    () =>
      (data?.pages.flatMap((p) => p.items) ?? [])
        .filter((m) => !m.isOwner)
        .filter((m) => !q || `${m.user.displayName} ${m.user.username}`.toLowerCase().includes(q.toLowerCase()))
        .slice(0, 6),
    [data, q],
  );
  const transfer = async (userId: string, name: string) => {
    const ok = await confirm({
      title: `Transfer ownership to ${name}?`,
      description: "They'll become the owner with every permission. You'll stay a member with your current roles and can't undo this yourself.",
      confirmLabel: "Transfer ownership",
      danger: true,
    });
    if (ok) await patch({ ownerId: userId }, "Ownership transferred");
  };
  return (
    <SettingsGroup title="Ownership" description="Hand the community to another member.">
      <div className="p-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members" aria-label="Search members" className="!h-9" />
      </div>
      {members.map((m) => (
        <div key={m.user.id} className="flex items-center gap-3 px-4 py-2">
          <Avatar src={m.user.avatarUrl} name={m.user.displayName} size="sm" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{m.user.displayName}</div>
            <div className="truncate text-xs text-fg-3">@{m.user.username}</div>
          </div>
          <Button size="sm" variant="ghost" icon={IconCrown} onClick={() => void transfer(m.user.id, m.user.displayName)}>
            Make owner
          </Button>
        </div>
      ))}
      {members.length === 0 && <p className="px-4 py-3 text-sm text-fg-3">No members match.</p>}
    </SettingsGroup>
  );
}

/* ───────────── Safety ───────────── */

const AGE_OPTIONS = [
  { value: "0", label: "No requirement" },
  { value: "1", label: "1 day" },
  { value: "3", label: "3 days" },
  { value: "7", label: "1 week" },
  { value: "30", label: "1 month" },
];

export function SafetySection({ structure }: SectionProps) {
  const c = structure.community;
  const patch = usePatchCommunity(c.id);
  const d = useDraft({ minAccountAgeDays: c.minAccountAgeDays ?? 0, blockLinks: c.blockLinks ?? false, blockedWords: c.blockedWords ?? [] });
  const [word, setWord] = useState("");
  const [saving, setSaving] = useState(false);
  const add = () => {
    const w = word.trim().toLowerCase();
    if (w && !d.draft.blockedWords.includes(w)) d.set("blockedWords", [...d.draft.blockedWords, w]);
    setWord("");
  };
  const save = async () => {
    setSaving(true);
    await patch(d.changed);
    setSaving(false);
  };
  return (
    <div>
      <SettingsGroup title="Chat filters" description="Roles with Bypass slow mode, administrators and the owner are exempt.">
        <SettingsRow title="Minimum account age" description="New accounts can read but not chat until they're this old.">
          <Select
            aria-label="Minimum account age"
            value={String(d.draft.minAccountAgeDays)}
            onChange={(v) => d.set("minAccountAgeDays", Number(v))}
            options={AGE_OPTIONS.some((o) => o.value === String(d.draft.minAccountAgeDays)) ? AGE_OPTIONS : [...AGE_OPTIONS, { value: String(d.draft.minAccountAgeDays), label: `${d.draft.minAccountAgeDays} days` }]}
          />
        </SettingsRow>
        <SettingsRow title="Block links" description="Reject messages that contain links." htmlFor="sf-links">
          <Switch id="sf-links" label="Block links" checked={d.draft.blockLinks} onCheckedChange={(v) => d.set("blockLinks", v)} />
        </SettingsRow>
      </SettingsGroup>

      <SettingsGroup title="Blocked words" description="Messages containing these words are rejected. Matching ignores case.">
        <div className="space-y-3 p-3">
          <div className="flex gap-2">
            <Input
              value={word}
              onChange={(e) => setWord(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add();
                }
              }}
              placeholder="Add a word"
              aria-label="Add a blocked word"
              maxLength={40}
              className="!h-9"
            />
            <Button onClick={add} disabled={!word.trim()}>
              Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {d.draft.blockedWords.map((w) => (
              <span key={w} className="inline-flex h-7 items-center gap-1 rounded-md bg-surface-2 pl-2 pr-1 font-mono text-sm">
                {w}
                <button aria-label={`Remove ${w}`} onClick={() => d.set("blockedWords", d.draft.blockedWords.filter((x) => x !== w))} className="rounded p-0.5 text-fg-3 hover:text-fg">
                  <IconX className="icon size-3.5" />
                </button>
              </span>
            ))}
            {d.draft.blockedWords.length === 0 && <span className="text-sm text-fg-3">No blocked words.</span>}
          </div>
        </div>
      </SettingsGroup>
      <p className="text-xs text-fg-3">Slow mode is set per channel, in each channel's settings.</p>
      <UnsavedBar show={d.dirty} saving={saving} onReset={d.reset} onSave={() => void save()} />
    </div>
  );
}
